import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../../software/api";
import { ArrowLeft, RotateCcw, Boxes, ImageOff, Palette, X, ShieldCheck } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const backend = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (u) => {
  if (!u) return "";
  const i = u.indexOf("/api/files/");
  if (i >= 0) return `${backend}${u.slice(i)}`;
  return u.startsWith("http") ? u : `${backend}${u}`;
};

// "Ash + $400" -> { label:"Ash", surcharge:400 }
const parseColour = (name) => {
  const m = String(name || "").match(/^(.*?)\s*\+\s*\$?([\d,]+)/);
  if (m) return { label: m[1].trim(), surcharge: Number(m[2].replace(/,/g, "")) };
  return { label: String(name || "").trim(), surcharge: 0 };
};
const isMicroban = (v) => v === true || v === "True" || v === "true" || v === 1;
const COLOUR_GROUPS = ["Shell", "Cabinet", "Cover"];

const EMPTY = {
  spa_type: "", model: "", shape: "",
  max_length: "", max_width: "", min_price: "", max_price: "",
  min_seats: "", min_recliners: "", min_jets: "", max_amperage: "", min_persons: "",
};

const money0 = (n) => `$${Number(n || 0).toLocaleString("en-AU")}`;

// A boxed "Label: value" cell like the MS Access form — compact
const Spec = ({ label, value, highlight }) => (
  <div className="grid grid-cols-[74px_1fr] items-center text-[11px] leading-tight">
    <span className="text-slate-700 text-right pr-1 font-semibold">{label}:</span>
    <span className={`px-1 py-0 border border-[#9db8d6] bg-white truncate ${highlight ? "!bg-[#fff29a] font-black text-center" : ""}`}>{value || "N/A"}</span>
  </div>
);

// Small top-of-row stat (Seats / Recliners / Jets)
const Stat = ({ label, value }) => (
  <div className="flex items-center gap-1 text-[12px]">
    <span className="font-bold text-slate-800">{label}:</span>
    <span className="px-1.5 py-0.5 border border-[#9db8d6] bg-white min-w-[38px] text-center font-mono2">{value}</span>
  </div>
);

// Filter field with label above a control
const FField = ({ label, children }) => (
  <label className="flex items-center justify-between gap-2 text-[12px] text-[#1a3a63] font-semibold">
    <span className="whitespace-nowrap">{label}</span>
    {children}
  </label>
);
const selCls = "border border-[#7ba7d9] bg-white text-slate-900 px-1.5 py-1 text-[12px] rounded-sm outline-none focus:border-[#123a63] min-w-[120px]";

function ColoursModal({ spa, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api("GET", "/colours?active_only=true").then(setItems).catch(() => setItems([])); }, []);

  const groups = useMemo(() => {
    const g = {};
    (items || []).forEach((c) => { const k = c.item || "Other"; (g[k] = g[k] || []).push(c); });
    const ordered = [];
    COLOUR_GROUPS.forEach((k) => { if (g[k]) { ordered.push([k, g[k]]); delete g[k]; } });
    Object.keys(g).sort().forEach((k) => ordered.push([k, g[k]]));
    return ordered;
  }, [items]);

  const printSheet = () => {
    const win = window.open("", "_blank", "width=900,height=1000");
    if (!win) return;
    const sections = groups.map(([grp, list]) => `
      <h2>${grp} Colours</h2>
      <div class="grid">${list.map((c) => { const pc = parseColour(c.name); return `
        <div class="sw">
          <div class="img">${c.image_url ? `<img src="${imgUrl(c.image_url)}" />` : ""}</div>
          <div class="nm">${pc.label}${isMicroban(c.microban) ? ' <span class="mb">Microban</span>' : ""}</div>
          <div class="sc">${pc.surcharge ? "+ $" + pc.surcharge.toLocaleString() : "No extra charge"}</div>
        </div>`; }).join("")}</div>`).join("");
    win.document.write(`<html><head><title>Colour Spec Sheet — ${spa?.name || ""}</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;color:#123a63;padding:24px;}
        h1{font-size:22px;margin:0 0 2px;} .sub{color:#64748b;font-size:13px;margin:0 0 18px;}
        h2{font-size:15px;border-bottom:2px solid #1a4f8a;padding-bottom:4px;margin:22px 0 10px;}
        .grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;}
        .sw{border:1px solid #cbd5e1;border-radius:6px;overflow:hidden;}
        .img{aspect-ratio:1/1;background:#f1f5f9;} .img img{width:100%;height:100%;object-fit:cover;}
        .nm{font-weight:700;font-size:12px;padding:6px 6px 0;} .sc{font-size:11px;color:#475569;padding:0 6px 6px;}
        .mb{background:#059669;color:#fff;font-size:9px;padding:1px 5px;border-radius:99px;}
      </style></head><body>
      <h1>${spa?.name || "Spa"} — Colour Options</h1>
      <p class="sub">${[spa?.brand, spa?.type].filter(Boolean).join(" · ")} &nbsp;|&nbsp; Glamour Pools &amp; Spas</p>
      ${sections}
      </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4" data-testid="spa-colours-modal" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white rounded-lg shadow-2xl max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between bg-gradient-to-b from-[#2a6cb5] to-[#1a4f8a] px-3 py-2">
          <span className="text-white font-black text-sm flex items-center gap-2"><Palette className="h-4 w-4" /> Available Colours{spa?.name ? ` — ${spa.name}` : ""}</span>
          <div className="flex items-center gap-2">
            <button onClick={printSheet} data-testid="spa-colours-print" className="text-white text-[12px] font-bold bg-white/15 hover:bg-white/25 rounded px-2.5 py-1">Print Spec Sheet</button>
            <button onClick={onClose} data-testid="spa-colours-close" className="text-white/90 hover:text-white"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="p-4 overflow-y-auto">
          {items === null ? <p className="text-slate-500 text-sm py-8 text-center">Loading colours…</p>
            : items.length === 0 ? <p className="text-slate-500 text-sm py-8 text-center">No colour swatches on file yet.</p>
            : groups.map(([grp, list]) => (
              <div key={grp} className="mb-4" data-testid={`spa-colour-group-${grp.toLowerCase()}`}>
                <p className="text-[13px] font-black text-[#123a63] border-b-2 border-[#1a4f8a] pb-1 mb-2">{grp} Colours <span className="text-slate-400 font-normal">({list.length})</span></p>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {list.map((c) => {
                    const pc = parseColour(c.name);
                    return (
                      <div key={c.id} data-testid={`spa-colour-${c.id}`} className="rounded-lg border border-slate-200 overflow-hidden bg-white">
                        <div className="aspect-square bg-slate-50 flex items-center justify-center relative">
                          {c.image_url ? <img src={imgUrl(c.image_url)} alt={pc.label} className="h-full w-full object-cover" /> : <ImageOff className="h-6 w-6 text-slate-300" />}
                          {isMicroban(c.microban) && <span title="Microban" className="absolute top-1 left-1 bg-emerald-600 text-white rounded-full h-5 w-5 flex items-center justify-center"><ShieldCheck className="h-3 w-3" /></span>}
                        </div>
                        <div className="p-1.5">
                          <p className="text-[12px] font-bold text-slate-800 truncate" title={pc.label}>{pc.label}</p>
                          <p className="text-[10px] font-semibold text-emerald-700">{pc.surcharge ? `+ $${pc.surcharge.toLocaleString()}` : "No extra charge"}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

export default function SpaSearch({ onExit, onNav }) {
  const [spas, setSpas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [stockOnly, setStockOnly] = useState(false);
  const [colours, setColours] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [opts, setOpts] = useState({});

  useEffect(() => {
    api("GET", "/spas/catalogue").then((r) => setSpas(r || [])).catch(() => setSpas([])).finally(() => setLoading(false));
    api("GET", "/catalogue/filter-options").then((r) => setOpts(r || {})).catch(() => setOpts({}));
  }, []);

  const spaTypes = useMemo(() => Array.from(new Set(spas.map((s) => s.spa?.spa_type).filter(Boolean))).sort(), [spas]);
  const shapes = useMemo(() => {
    const pool = f.spa_type ? spas.filter((s) => s.spa?.spa_type === f.spa_type) : spas;
    return Array.from(new Set(pool.map((s) => s.spa?.shape).filter(Boolean))).sort();
  }, [spas, f.spa_type]);
  const models = useMemo(() => {
    let pool = spas;
    if (f.spa_type) pool = pool.filter((s) => s.spa?.spa_type === f.spa_type);
    if (f.shape) pool = pool.filter((s) => s.spa?.shape === f.shape);
    return Array.from(new Set(pool.map((s) => s.spa?.model || s.name).filter(Boolean))).sort();
  }, [spas, f.spa_type, f.shape]);
  // Clear a Model/Shape selection that no longer belongs to the chosen Spa Type
  useEffect(() => { if (f.model && !models.includes(f.model)) setF((x) => ({ ...x, model: "" })); }, [models, f.model]);
  useEffect(() => { if (f.shape && !shapes.includes(f.shape)) setF((x) => ({ ...x, shape: "" })); }, [shapes, f.shape]);

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const clear = () => { setF(EMPTY); setQ(""); setStockOnly(false); };

  const shown = useMemo(() => spas.filter((p) => {
    const s = p.spa || {};
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      const hay = [s.model, p.name, s.brand, s.spa_type, p.sku].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    if (f.spa_type && s.spa_type !== f.spa_type) return false;
    if (f.model && (s.model || p.name) !== f.model) return false;
    if (f.shape && s.shape !== f.shape) return false;
    if (f.max_length && Number(s.length || 0) > Number(f.max_length)) return false;
    if (f.max_width && Number(s.width || 0) > Number(f.max_width)) return false;
    if (f.min_price && Number(s.retail_price || p.price || 0) < Number(f.min_price)) return false;
    if (f.max_price && Number(s.retail_price || p.price || 0) > Number(f.max_price)) return false;
    if (Number(f.min_seats) && Number(s.seats || 0) < Number(f.min_seats)) return false;
    if (Number(f.min_recliners) && Number(s.recliners || 0) < Number(f.min_recliners)) return false;
    if (Number(f.min_jets) && Number(s.jets || 0) < Number(f.min_jets)) return false;
    if (f.max_amperage && Number(s.amps || 0) > Number(f.max_amperage)) return false;
    if (Number(f.min_persons) && Number(s.persons || 0) < Number(f.min_persons)) return false;
    if (stockOnly && Number(p.qty || 0) <= 0) return false;
    return true;
  }).sort((a, b) => (Number(a.spa?.retail_price || a.price || 0)) - (Number(b.spa?.retail_price || b.price || 0))), [spas, f, q, stockOnly]);

  const goStock = useCallback(() => { onNav ? onNav("tab:inventory") : setStockOnly(true); }, [onNav]);

  return (
    <div data-testid="spa-search-panel" className="rounded-xl overflow-hidden" style={{ background: "linear-gradient(180deg,#dcecfa,#bcd8f0)" }}>
      {/* Title bar */}
      <div className="flex items-center gap-2 bg-[#1a4f8a] px-3 py-1.5">
        <Boxes className="h-4 w-4 text-white" />
        <span className="text-white font-black text-sm">Spa Search — Spas · Swim Spas · Ice Baths · Urban Pools</span>
        <div className="ml-auto flex items-center gap-1.5">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type a spa name or model…"
            data-testid="spa-text-search"
            className="w-56 rounded-sm border border-[#7ba7d9] bg-white px-2 py-1 text-[12px] text-slate-900 outline-none focus:border-white"
          />
          {q && (
            <button onClick={() => setQ("")} data-testid="spa-text-search-clear" title="Clear search" className="text-white/90 hover:text-white"><X className="h-4 w-4" /></button>
          )}
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="p-3">
        <div className="rounded-lg border border-[#7ba7d9] bg-[#cfe0f4] p-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 items-start">
            {/* Col 1 — dropdowns */}
            <div className="space-y-2">
              <FField label="Spa Type">
                <select value={f.spa_type} onChange={set("spa_type")} data-testid="spa-f-type" className={selCls}>
                  <option value="">All</option>{spaTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </FField>
              <FField label="Model">
                <select value={f.model} onChange={set("model")} data-testid="spa-f-model" className={selCls}>
                  <option value="">All</option>{models.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </FField>
              <FField label="Shape">
                <select value={f.shape} onChange={set("shape")} data-testid="spa-f-shape" className={selCls}>
                  <option value="">All</option>{shapes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </FField>
            </div>
            {/* Col 2 — dimensions & price */}
            <div className="space-y-2">
              <FField label="Maximum Length"><select value={f.max_length} onChange={set("max_length")} data-testid="spa-f-maxlen" className={selCls}><option value="">All</option>{(opts.max_length || []).map((n) => <option key={n} value={n}>{n}</option>)}</select></FField>
              <FField label="Maximum Width"><select value={f.max_width} onChange={set("max_width")} data-testid="spa-f-maxwid" className={selCls}><option value="">All</option>{(opts.max_width || []).map((n) => <option key={n} value={n}>{n}</option>)}</select></FField>
              <FField label="Minimum Price"><select value={f.min_price} onChange={set("min_price")} data-testid="spa-f-minprice" className={selCls}><option value="">All</option>{(opts.min_price || []).map((n) => <option key={n} value={n}>{money0(n)}</option>)}</select></FField>
              <FField label="Maximum Price"><select value={f.max_price} onChange={set("max_price")} data-testid="spa-f-maxprice" className={selCls}><option value="">All</option>{(opts.max_price || []).map((n) => <option key={n} value={n}>{money0(n)}</option>)}</select></FField>
            </div>
            {/* Col 3 — minimums */}
            <div className="space-y-2">
              <FField label="Minimum Seats"><select value={f.min_seats} onChange={set("min_seats")} data-testid="spa-f-minseats" className={selCls}><option value="">All</option>{(opts.min_seats || []).map((n) => <option key={n} value={n}>{n}</option>)}</select></FField>
              <FField label="Minimum Recliners"><select value={f.min_recliners} onChange={set("min_recliners")} data-testid="spa-f-minrec" className={selCls}><option value="">All</option>{(opts.min_recliners || []).map((n) => <option key={n} value={n}>{n}</option>)}</select></FField>
              <FField label="Minimum Jets"><select value={f.min_jets} onChange={set("min_jets")} data-testid="spa-f-minjets" className={selCls}><option value="">All</option>{(opts.min_jets || []).map((n) => <option key={n} value={n}>{n}</option>)}</select></FField>
              <FField label="Maximum Amperage"><select value={f.max_amperage} onChange={set("max_amperage")} data-testid="spa-f-maxamp" className={selCls}><option value="">All</option>{(opts.max_amps || []).map((n) => <option key={n} value={n}>{n}</option>)}</select></FField>
            </div>
            {/* Col 4 — persons + buttons */}
            <div className="flex flex-col gap-2">
              <div className="text-center">
                <p className="text-[12px] font-black text-[#1a3a63] mb-1">Minimum Number of Persons</p>
                <select value={f.min_persons} onChange={set("min_persons")} data-testid="spa-f-minpersons" className="bg-[#fff29a] text-slate-900 border border-[#c9a800] font-black text-lg px-4 py-1 rounded-sm text-center outline-none">
                  <option value="">All</option>{(opts.min_persons || []).map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="grid gap-1.5 mt-1">
                <button onClick={clear} data-testid="spa-clear-filters" className="rounded-sm border-2 border-[#1a4f8a] bg-white px-3 py-1.5 text-[13px] font-black text-[#1a4f8a] hover:bg-sky-50 inline-flex items-center justify-center gap-1.5"><RotateCcw className="h-3.5 w-3.5" /> Clear Filters</button>
                <button onClick={() => (onExit ? onExit() : null)} data-testid="spa-return-switchboard" className="rounded-sm border-2 border-[#123a63] bg-white px-3 py-1.5 text-[13px] font-black text-[#123a63] hover:bg-slate-50 inline-flex items-center justify-center gap-1.5"><ArrowLeft className="h-3.5 w-3.5" /> Return to Switchboard</button>
                <button onClick={goStock} data-testid="spa-view-stock" className="rounded-sm border-2 border-emerald-700 bg-white px-3 py-1.5 text-[13px] font-black text-emerald-700 hover:bg-emerald-50 inline-flex items-center justify-center gap-1.5"><Boxes className="h-3.5 w-3.5" /> View Stock Spas</button>
              </div>
            </div>
          </div>
          <p className="text-[11px] text-[#1a3a63] mt-2 font-semibold" data-testid="spa-result-count">{loading ? "Loading spas…" : `${shown.length} spa${shown.length === 1 ? "" : "s"} match your filters.`}</p>
        </div>

        {/* RESULT ROWS */}
        <div className="mt-3 space-y-2">
          {!loading && shown.length === 0 && (
            <div className="rounded-lg border border-dashed border-[#7ba7d9] bg-white/70 p-8 text-center text-slate-500 text-sm">No spas match your filters. Try Clear Filters.</div>
          )}
          {shown.map((p) => {
            const s = p.spa || {};
            return (
              <div key={p.id} data-testid="spa-row" className="rounded-md border border-[#7ba7d9] bg-[#e8f1fb] p-2">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_150px_320px] gap-2 items-stretch">
                  {/* Col A — name + core dims */}
                  <div className="space-y-1">
                    <div className="text-center font-black text-[#123a63] underline text-[14px] truncate" title={p.name}>{s.model || p.name}</div>
                    <Spec label="Length" value={s.length} />
                    <Spec label="Width" value={s.width} />
                    <Spec label="Height" value={s.height} />
                    <Spec label="Pumps" value={s.pumps} />
                    <Spec label="Pump Size" value={s.pump_size} />
                    <Spec label="Speed" value={s.speed} />
                  </div>
                  {/* Col B — persons + features */}
                  <div className="space-y-1">
                    <div className="grid grid-cols-[92px_1fr] items-center text-[12px]">
                      <span className="text-slate-700 text-right pr-1.5 font-black">No Persons:</span>
                      <span className="px-1.5 py-0.5 border border-[#c9a800] bg-[#fff29a] font-black text-center">{s.persons || 0}</span>
                    </div>
                    <Spec label="Cycle Filtration" value={s.cycle_filtration} />
                    <Spec label="Ozone" value={s.ozone} />
                    <Spec label="Air Pump" value={s.air_pump} />
                    <Spec label="Stainless Jets" value={s.stainless_jets} />
                    <Spec label="Insulated Pipes" value={s.insulated_pipes} />
                    <Spec label="Aroma Therapy" value={s.aroma_therapy} />
                  </div>
                  {/* Col C — stats + more features */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between gap-1 mb-0.5">
                      <Stat label="Seats" value={s.seats || 0} />
                      <Stat label="Recl." value={s.recliners || 0} />
                      <Stat label="Jets" value={s.jets || 0} />
                    </div>
                    <Spec label="Tuff Base" value={s.tuff_base} />
                    <Spec label="Nk Jets" value={s.moulded_neck_jets} />
                    <Spec label="Waterfall" value={s.waterfall} />
                    <Spec label="Load Shed" value={s.load_shed} />
                    <Spec label="Full Load" value={s.full_load} />
                    <Spec label="Run Cost" value={s.running_cost} />
                  </div>
                  {/* Col D — price + colours */}
                  <div className="flex flex-col items-center justify-center gap-1 px-1">
                    <span className="text-[11px] text-slate-600 font-semibold">Retail Price</span>
                    <span className="w-full text-center border border-[#9db8d6] bg-white px-2 py-1 font-black text-[#123a63]">{money(s.retail_price || p.price)}</span>
                    <span className="mt-1 text-[10px] text-slate-500 font-semibold">{s.spa_type}{p.qty > 0 ? ` · ${p.qty} on floor` : ""}</span>
                    <button onClick={() => setColours({ name: s.model || p.name, brand: s.brand, type: s.spa_type })} data-testid={`spa-colours-btn-${p.id}`} className="mt-1 w-full rounded-sm bg-[#2a6cb5] text-white font-black text-[12px] py-2 hover:bg-[#1a4f8a] inline-flex items-center justify-center gap-1.5"><Palette className="h-3.5 w-3.5" /> AVAILABLE COLOURS</button>
                  </div>
                  {/* Col E — image (click to enlarge) */}
                  <button type="button" disabled={!p.image_url}
                    onClick={() => p.image_url && setLightbox({ url: imgUrl(p.image_url), name: s.model || p.name })}
                    data-testid={`spa-image-${p.id}`} title={p.image_url ? "Click to enlarge" : ""}
                    className="w-full min-h-[200px] h-full bg-white border border-[#9db8d6] overflow-hidden flex items-center justify-center group cursor-zoom-in disabled:cursor-default">
                    {p.image_url ? <img src={imgUrl(p.image_url)} alt={p.name} className="h-full w-full object-contain group-hover:scale-[1.04] transition-transform duration-200" /> : <ImageOff className="h-8 w-8 text-slate-300" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {colours && <ColoursModal spa={colours} onClose={() => setColours(null)} />}
      {lightbox && (
        <div className="fixed inset-0 z-[130] bg-black/80 flex items-center justify-center p-4" data-testid="spa-image-lightbox" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} data-testid="spa-image-lightbox-close" className="absolute top-4 right-4 text-white/80 hover:text-white"><X className="h-8 w-8" /></button>
          <div className="flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.name} className="max-w-[92vw] max-h-[82vh] object-contain rounded-md shadow-2xl bg-white" />
            <p className="text-white font-bold text-sm">{lightbox.name}</p>
          </div>
        </div>
      )}
    </div>
  );
}
